import { Form, FormikErrors } from 'formik';
import { FormikDebounce } from '../../../common/form/FormikDebounce';
import { FC, useState } from 'react';
import { Box, Collapse, Fade, IconButton, Paper, Tooltip } from '@mui/material';
import { FormContainer } from '../../../common/form/FormContainer';
import { DatePicker } from '../../../common/form/DatePicker';
import { Input } from '../../../common/form/Input';
import { SubmitButton } from '../../../common/form/SubmitButton';
import { EmployeeAutocomplete } from '../../../common/form/EmployeeAutocomplete';
import { SupplierEmployeeAutocomplete } from '../../../common/form/SupplierEmployeeAutocomplete';
import { QcsSwitch } from '../../../common/form/QcsSwitch';
import { QcsTypography } from '../../../common/basic/QcsTypography';
import { ReadOnlyField } from '../../../common/basic/ReadOnlyField';
import { invitationGetUpdateDto, useInvitation } from './invitationFunctions';
import * as Yup from 'yup';
import { validations } from '../../../../utils/validations';
import { useAppSelector } from '../../../../store';
import { selectIdentity } from '../../../../store/entities/identity';
import { hasRole } from '../../../../utils/roles';
import { useBusinessSettings } from '../../../../hooks/useBusinessSettings';
import { invitationService, workplaceService } from '@api/services/services';
import { QcsTimePicker } from '../../../common/form/QcsTimePicker';
import { InvitationDto } from '@api/models/invitation/InvitationDto';
import { UserRole } from '@api/models/enums/UserRole';
import { ENTERABLE_USER_STATES } from '@api/models/enums/UserState';
import { InvitationState } from '@api/models/enums/InvitationState';
import { ShowError } from '../../../ShowError';
import { resources } from '@api/resources';
import { formatDate } from '../../../../utils/date';
import { QcsLoadingButton } from '../../../common/basic/QcsLoadingButton';
import { QcsButton } from '../../../common/basic/QcsButton';
import { invitationListActions } from '../../../../store/entities/invitationList';
import { PathKey, useNavigation } from '../../../../utils/urlPaths';
import { ResponsiveRow } from '../../../common/form/ResponsiveRow';
import { FaIcon } from '../../../common/FaIcon';
import { faBellSlash, faDoorOpen, faEdit, faFileSignature, faSignInAlt, faUsers } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { SelectResponsiblePersonModal } from '../SelectResponsiblePersonModal';
import { GeneralInfoModal } from '../../../common/GeneralInfoModal';
import { ConfirmDialog } from '../../../common/ConfirmDialog';
import { SupplierEmployeeDto } from '@api/models/suppliers/SupplierEmployeeDto';
import { PersonLink } from '../PersonLink';
import { sectionLabelSx } from '../styles';

const TRANSITION_MS = 300;

export const InvTabDetail: FC = () => {
  const { t, i18n, invitation, callWithLock, reloadInvitation, dispatch, isOneTime } = useInvitation();
  const identity = useAppSelector(selectIdentity);
  const businessSettings = useBusinessSettings();
  const { goBack, navigate, currentPath } = useNavigation('/invitation');
  const [editing, setEditing] = useState(false);
  const [unarchiveModalOpen, setUnarchiveModalOpen] = useState(false);
  const [showExpiredWarning, setShowExpiredWarning] = useState(false);
  const [resendConfirmOpen, setResendConfirmOpen] = useState(false);

  const handleSubmit = async (data: InvitationDto) => {
    await callWithLock(async () => {
      const data2 = invitationGetUpdateDto(data);
      await invitationService.update(invitation.id!, data2);
      await reloadInvitation();
    });
    setEditing(false);
  };

  const handleArchive = async () => {
    const deleted = await invitationService.deleteById(invitation.id!);
    if (!deleted) return;
    dispatch(invitationListActions.reload(true));
    goBack();
  };

  const doUnarchive = async (responsiblePersonOverride?: SupplierEmployeeDto) => {
    await callWithLock(async () => {
      await invitationService.unarchive(invitation.id!, {
        responsiblePersonId: responsiblePersonOverride?.id,
      });
      await reloadInvitation();
    });
    if (invitation.validTo && new Date(invitation.validTo) < new Date()) {
      setShowExpiredWarning(true);
    }
  };

  const handleUnarchive = async () => {
    // The backend refuses to unarchive with a responsible person who can no longer enter the site
    // and expects a replacement instead, so ask for one up front rather than let it fail.
    const responsiblePersonState = invitation.responsiblePerson?.state;
    if (responsiblePersonState && !ENTERABLE_USER_STATES.includes(responsiblePersonState)) {
      setUnarchiveModalOpen(true);
    } else {
      await doUnarchive();
    }
  };

  const handleResendToSupplier = async () => {
    await callWithLock(async () => {
      await invitationService.sendNotification(invitation.id!);
    });
  };

  const isInternalRole = hasRole(identity.roles, [UserRole.AdminCompany, UserRole.ManagerOsah, UserRole.ManagerWorkplace]);
  const canEdit = hasRole(identity.roles, [UserRole.ManagerOsah, UserRole.ManagerWorkplace]);
  const disableEdit = !canEdit || invitation.state === InvitationState.Archived;

  const handleNotifyCheckboxClicked = (
    checked: boolean,
    setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => Promise<void | FormikErrors<InvitationDto>>,
  ) => {
    if (!checked || !!(invitation.notifyEmployees && invitation.notifyEmployees.length > 0)) return;
    const workplaceIds = invitation.workplaces?.map((w) => w.workplace?.id).filter((id): id is string => id !== undefined);
    if (!workplaceIds) return;
    workplaceService.getWorkplaceNotificationEmployees(workplaceIds).then((it) => {
      setFieldValue('notifyEmployees', it.data);
    });
  };

  const employee = invitation.employee;
  const responsiblePerson = invitation.responsiblePerson;

  const canArchive =
    invitation.state !== InvitationState.Archived &&
    hasRole(identity.roles, [UserRole.AdminQcs, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.Receptionist]);

  const handleEmployeeClick = () => {
    if (employee?.id) {
      navigate(`/employee/${employee.id}?backTo=${encodeURIComponent(currentPath)}`);
    }
  };

  const handleResponsiblePersonClick = () => {
    if (responsiblePerson?.id && invitation.supplier?.id) {
      navigate(PathKey.SupplierEmployee, { supplierId: invitation.supplier.id, employeeId: responsiblePerson.id }, { backTo: currentPath });
    }
  };

  const activeSettings: { icon: IconDefinition; label: string }[] = [];
  if (invitation.disableVisitationNotification) activeSettings.push({ icon: faBellSlash, label: t('common.disableVisitationNotification') });
  if (invitation.isNotifyEmployees) activeSettings.push({ icon: faUsers, label: t('invitation.notifyEmployees') });
  if (invitation.createHandoverProtocol) activeSettings.push({ icon: faFileSignature, label: t('invitation.createHandoverProtocol') });
  if (invitation.turnstilesActive) activeSettings.push({ icon: faDoorOpen, label: t('common.turnstilesActive') });
  if (invitation.entryRequestRequired) activeSettings.push({ icon: faSignInAlt, label: t('invitation.entryRequestRequired') });

  return (
    <>
      <FormikDebounce<InvitationDto>
        enableReinitialize
        initialValues={{
          ...invitation,
          disableVisitationNotification: invitation.disableVisitationNotification ?? false,
          createHandoverProtocol: invitation.createHandoverProtocol ?? false,
          turnstilesActive: invitation.turnstilesActive ?? false,
          employee: invitation.employee,
          notifyEmployees: invitation.notifyEmployees ?? [],
          isNotifyEmployees: (invitation.notifyEmployees && invitation.notifyEmployees.length > 0) ?? false,
          earliestEntryTime: invitation.earliestEntryTime ?? '',
          entryRequestRequired: invitation.entryRequestRequired ?? false,
        }}
        validationSchema={Yup.object({
          validFrom: validations.stringDateNotPastIfChangedRequired(t, invitation.validFrom!),
          validTo: Yup.string()
            .nullable()
            .when('validFrom', ([validFrom], schema) =>
              schema.test({
                test: (validTo: string | null | undefined) => {
                  return !validTo || validFrom <= validTo;
                },
                message: t('errors.dateToBeforeFrom')!,
              }),
            ),
          employee: validations.objectRequired(t),
        })}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <FormContainer>
              {/* ── Read-only summary ── */}
              <Collapse in={!editing} timeout={TRANSITION_MS} unmountOnExit>
                <Fade in={!editing} timeout={TRANSITION_MS}>
                  <Paper
                    variant="outlined"
                    sx={{
                      mt: 2,
                      p: 2,
                      borderRadius: 2,
                      borderColor: 'divider',
                    }}
                  >
                    {/* Header row with edit button */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <QcsTypography variant="body2" sx={sectionLabelSx}>
                        {t('invitation.employee')}
                      </QcsTypography>
                      {!disableEdit && (
                        <Tooltip title={t('invitation.edit')}>
                          <IconButton size="small" onClick={() => setEditing(true)} sx={{ transition: 'background-color 0.2s' }}>
                            <FaIcon icon={faEdit} width={14} height={14} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <PersonLink person={employee} onClick={employee?.id && isInternalRole ? handleEmployeeClick : undefined} />

                    {/* Responsible person */}
                    {responsiblePerson && !isOneTime && (
                      <Box sx={{ mt: 2 }}>
                        <QcsTypography variant="body2" sx={sectionLabelSx}>
                          {t('invitation.responsibleSupplierPerson')}
                        </QcsTypography>
                        <PersonLink
                          person={responsiblePerson}
                          onClick={responsiblePerson.id && invitation.supplier?.id ? handleResponsiblePersonClick : undefined}
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    )}

                    {/* Dates row */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 2 }}>
                      <Box>
                        <ReadOnlyField label={t('training.validFrom')} valueSx={{ fontWeight: 500 }}>
                          {formatDate(i18n, invitation.validFrom) || '—'}
                        </ReadOnlyField>
                      </Box>
                      <Box>
                        <ReadOnlyField label={t('training.validTo')} valueSx={{ fontWeight: 500 }}>
                          {formatDate(i18n, invitation.validTo) || '—'}
                        </ReadOnlyField>
                      </Box>
                      {invitation.earliestEntryTime && (
                        <Box>
                          <ReadOnlyField label={t('invitation.earliestEntryTime')} valueSx={{ fontWeight: 500 }}>
                            {invitation.earliestEntryTime}
                          </ReadOnlyField>
                        </Box>
                      )}
                    </Box>

                    {/* Notes */}
                    {(invitation.note || invitation.noteInternal) && (
                      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {invitation.noteInternal && (
                          <Box>
                            <ReadOnlyField label={t('invitation.noteInternal')} valueSx={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
                              {invitation.noteInternal}
                            </ReadOnlyField>
                          </Box>
                        )}
                        {invitation.note && (
                          <Box>
                            <ReadOnlyField label={t('invitation.note')} valueSx={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
                              {invitation.note}
                            </ReadOnlyField>
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Active settings summary */}
                    {activeSettings.length > 0 && isInternalRole && (
                      <Box sx={{ mt: 2 }}>
                        <ReadOnlyField label={t('mainMenu.additionalSettings')} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.25 }}>
                          {activeSettings.map((setting, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <FaIcon icon={setting.icon} width={12} height={12} style={{ opacity: 0.55 }} />
                              <QcsTypography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                {setting.label}
                              </QcsTypography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Notify employees list */}
                    {invitation.notifyEmployees && invitation.notifyEmployees.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <QcsTypography variant="body2" sx={sectionLabelSx}>
                          {t('workplace.notificationEmployees')}
                        </QcsTypography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                          {invitation.notifyEmployees.map((emp) => (
                            <PersonLink
                              key={emp.id}
                              person={emp}
                              onClick={emp.id ? () => navigate(`/employee/${emp.id}?backTo=${encodeURIComponent(currentPath)}`) : undefined}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Paper>
                </Fade>
              </Collapse>

              {/* ── Edit form ── */}
              <Collapse in={editing} timeout={TRANSITION_MS} unmountOnExit>
                <Fade in={editing} timeout={TRANSITION_MS}>
                  <Box>
                    <Paper
                      variant="outlined"
                      sx={{
                        mt: 2,
                        p: 2,
                        borderRadius: 2,
                        borderColor: 'primary.light',
                      }}
                    >
                      <FormContainer>
                        <ResponsiveRow>
                          <DatePicker name="validFrom" label={t('training.validFrom')} required={true} disablePast={true} />
                          <DatePicker name="validTo" label={t('training.validTo')} disablePast={true} />
                          <QcsTimePicker label={t('invitation.earliestEntryTime')} name="earliestEntryTime" />
                        </ResponsiveRow>
                        <EmployeeAutocomplete name="employee" label={t('invitation.employee')} required filterOutNonHumanRoles={true} />
                        {!isOneTime && (
                          <SupplierEmployeeAutocomplete
                            name="responsiblePerson"
                            label={t('invitation.responsibleSupplierPerson')}
                            required
                            supplierId={invitation.supplier?.id}
                            role={UserRole.ExternalManager}
                            state={ENTERABLE_USER_STATES}
                          />
                        )}
                        <Input name="note" label={t('invitation.note')} maxLength={5000} multiline={true} rows={4} />
                        <Input name="noteInternal" label={t('invitation.noteInternal')} maxLength={5000} multiline={true} rows={4} />
                        <QcsSwitch
                          name="disableVisitationNotification"
                          label={t('common.disableVisitationNotification')}
                          onChange={(value) => {
                            if (value) {
                              setFieldValue('isNotifyEmployees', false);
                              setFieldValue('notifyEmployees', []);
                            }
                          }}
                        />
                        {!values.disableVisitationNotification && (
                          <>
                            <QcsSwitch
                              label={t('invitation.notifyEmployees')}
                              name={'isNotifyEmployees'}
                              onChange={(value) => handleNotifyCheckboxClicked(value, setFieldValue)}
                            />
                            {values.isNotifyEmployees && (
                              <EmployeeAutocomplete
                                name="notifyEmployees"
                                label={t('workplace.notificationEmployees')}
                                filterOutNonHumanRoles
                                multiple
                              />
                            )}
                          </>
                        )}

                        {!isOneTime && businessSettings?.workplaceHandoverEnabled && (
                          <QcsSwitch name="createHandoverProtocol" label={t('invitation.createHandoverProtocol')} />
                        )}
                        {businessSettings?.turnstilesEnabled && <QcsSwitch name="turnstilesActive" label={t('common.turnstilesActive')} />}
                        {!isOneTime && businessSettings?.allowEntryRequests && (
                          <QcsSwitch name="entryRequestRequired" label={t('invitation.entryRequestRequired')} />
                        )}

                        <ShowError resource={resources.updateInvitation} />

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
                          <SubmitButton text="common.save" />
                          <QcsButton variant="outlined" onClick={() => setEditing(false)}>
                            {t('common.cancel')}
                          </QcsButton>
                        </Box>
                      </FormContainer>
                    </Paper>
                  </Box>
                </Fade>
              </Collapse>

              {/* Action buttons - internal roles only */}
              {canEdit && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
                  <QcsButton variant="outlined" onClick={() => setResendConfirmOpen(true)} disabled={disableEdit}>
                    {t(isOneTime ? 'invitation.resendToAll' : 'invitation.resendToSupplier')}
                  </QcsButton>
                  {canArchive && (
                    <QcsLoadingButton resourceLoading={resources.deleteInvitation} onClick={handleArchive} variant="outlined" color="error">
                      {t('invitation.archive')}
                    </QcsLoadingButton>
                  )}
                  {invitation.state === InvitationState.Archived &&
                    hasRole(identity.roles, [UserRole.Receptionist, UserRole.ManagerOsah, UserRole.ManagerWorkplace]) && (
                      <QcsButton onClick={handleUnarchive} variant="outlined">
                        {t('invitation.unarchive')}
                      </QcsButton>
                    )}
                </Box>
              )}
            </FormContainer>
          </Form>
        )}
      </FormikDebounce>

      <SelectResponsiblePersonModal
        open={unarchiveModalOpen}
        title="invitation.unarchiveSelectResponsiblePerson"
        onClose={() => setUnarchiveModalOpen(false)}
        onSubmit={async (responsiblePerson) => {
          await doUnarchive(responsiblePerson);
          setUnarchiveModalOpen(false);
        }}
      />
      <ConfirmDialog
        onClose={() => setResendConfirmOpen(false)}
        onConfirm={resendConfirmOpen ? handleResendToSupplier : null}
        title={t(isOneTime ? 'invitation.resendToAll' : 'invitation.resendToSupplier')}
        message={t(isOneTime ? 'invitation.resendToAllConfirm' : 'invitation.resendToSupplierConfirm')}
      />
      <GeneralInfoModal
        open={showExpiredWarning}
        onClose={() => setShowExpiredWarning(false)}
        text={
          <>
            {t('invitation.unarchiveExpiredWarningBefore')}
            <strong>{t('training.validTo')}</strong>
            {t('invitation.unarchiveExpiredWarningAfter')}
          </>
        }
        variant="info"
      />
    </>
  );
};
